import { Checkbox } from '@shared/ui/checkbox';
import { Label } from '@shared/ui/label';
import { AddressLines, CityField, RegionSelect, CountrySelect, PostalCodeField } from '@shared/components/AddressFields/AddressFields';
import { useShippingAddress } from '../../hooks/useShippingAddress';
import { useUserStore } from '../../../auth/store/userStore';
import { sectionHeading } from '../profileStyles';
import { checkboxLabelClassName, dropdownErrorClassName } from '@shared/ui/formStyles';

/**
 * Shipping Address ("Site Address" in HaloCRM) — shown for account-owner/billing-admin, same
 * gating as BillingAddressSection (see ProfileInfo.jsx's canEditBillingAndContact). Country is
 * shared with Billing above and rendered here as a disabled mirror, matching the checkout page's
 * shipping card. Also carries the same "Same as billing address" checkbox as the checkout page
 * and the Edit Billing Details dialog, and like the checkout page hides the fields while it is ticked.
 */
export function ShippingAddressSection({ canEditBillingAndContact, register, setValue, watch, getValues, errors, isSubmitted = false, dataTestId }) {
  const baseTestId = dataTestId || 'profile-shipping-section';
  const { profile, selectedCountry } = useUserStore();
  const {
    availableRegions,
    loadingRegions,
    handleShippingRegionChange,
    shippingSameAsBilling,
    handleShippingSameAsBillingChange,
  } = useShippingAddress({ setValue, watch, getValues, isSubmitted });

  // `selectedCountry` (useUserStore) is kept live by useBillingAddress as the user changes the
  // Country dropdown in BillingAddressSection above — reading it here instead of
  // profile.billingAddress.country means this mirror updates immediately instead of staying on
  // whatever country was last actually saved. Falls back to the saved value for the brief window
  // before that effect has run.
  const billingCountryLabel = selectedCountry?.label || profile?.billingAddress?.country || '';
  const fieldsDisabled = !canEditBillingAndContact;

  return (
    <div className="space-y-4" data-testid={baseTestId}>
      {/* The heading stays visible even while the fields below are hidden — it is what tells the
          user which address the "Same as billing address" checkbox under it refers to. */}
      <h3 className={sectionHeading} data-testid={`${baseTestId}-header`}>Shipping Address</h3>

      <div className="flex items-center gap-2" data-testid={`${baseTestId}-same-as-billing-container`}>
        <Checkbox
          id={`${baseTestId}-same-as-billing`}
          checked={shippingSameAsBilling}
          onCheckedChange={handleShippingSameAsBillingChange}
          disabled={!canEditBillingAndContact}
          dataTestId={`${baseTestId}-same-as-billing-checkbox`}
        />
        <Label htmlFor={`${baseTestId}-same-as-billing`} className={checkboxLabelClassName} data-testid={`${baseTestId}-same-as-billing-label`}>
          Same as billing address
        </Label>
      </div>

      {/* Hidden (unmounted) while "same as billing" is ticked, as on the checkout page.
          react-hook-form keeps unmounted fields' values (shouldUnregister defaults to false), so
          useShippingAddress's billing → shipping sync, validation and the save payload all keep
          working on the copied values; unticking remounts the fields pre-filled with that copy. */}
      {!shippingSameAsBilling && (
        <div className="space-y-4" data-testid={`${baseTestId}-fields`}>
          <AddressLines
            namePrefix="shipping"
            register={register}
            errors={errors}
            disabled={fieldsDisabled}
            dataTestId={`${baseTestId}-form`}
          />

          {/* City | Region/State | Post/ZIP Code, then Country on its own row — the same grid as
              Billing above and the checkout review page (see AddressLines). */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <CityField
              namePrefix="shipping"
              register={register}
              errors={errors}
              disabled={fieldsDisabled}
              dataTestId={`${baseTestId}-form`}
            />
            <RegionSelect
              id="shippingRegion"
              label="Region/State"
              value={watch('shippingRegion')}
              onValueChange={handleShippingRegionChange}
              options={availableRegions}
              loading={loadingRegions}
              disabled={fieldsDisabled || !watch('country')}
              error={errors?.shippingRegion?.message}
              errorClassName={dropdownErrorClassName}
              dataTestId={`${baseTestId}-region`}
            />
            <PostalCodeField
              namePrefix="shipping"
              register={register}
              errors={errors}
              label="Post/ZIP Code"
              disabled={fieldsDisabled}
              dataTestId={`${baseTestId}-form`}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <CountrySelect
              id="shippingCountry"
              value={billingCountryLabel}
              options={billingCountryLabel ? [{ value: billingCountryLabel, label: billingCountryLabel }] : []}
              mirrored
              dataTestId={`${baseTestId}-country`}
            />
          </div>
        </div>
      )}
    </div>
  );
}
